import { Button, Menu, MenuButton, MenuList } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import {
  bffApi,
  type ContractTaskDto,
  type ListContractTasksForOrganizationApiArg,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { ListDownloadItems, listSelection, type ListFormat } from "../../../common/download/ListDownloadItems";
import {
  EXPORT_PAGE_SIZE,
  ExportTooLargeError,
  exportRequest,
  exportTooLarge,
  isTooLargeToDownload,
  pdfTooLarge,
} from "../../../common/download/listExport";
import { fetchFresh } from "../../../common/redux/fetchFresh";
import { useAppDispatch } from "../../../common/redux/hooks";
import { taskCell, taskListColumns, taskListFileName } from "./taskListExport";

export type TaskListQuery = Omit<ListContractTasksForOrganizationApiArg, "limit" | "after">;

export const TaskListDownloadMenu = ({
  query,
  isEmpty,
  isLoading,
  failed,
  filters,
}: {
  query: TaskListQuery;
  isEmpty: boolean;
  isLoading: boolean;
  failed: boolean;
  filters: string[];
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const authState = useLoggedInWithOrgContextState();
  const columns = taskListColumns(t);

  const buildBody = async (format: ListFormat) => {
    const tooLarge = format === "PDF" ? pdfTooLarge : exportTooLarge;
    const listed: ContractTaskDto[] = [];
    let after: number | undefined;
    do {
      const page = await fetchFresh(
        dispatch(
          bffApi.endpoints.listContractTasksForOrganization.initiate(
            { ...query, after, limit: EXPORT_PAGE_SIZE },
            { forceRefetch: true },
          ),
        ),
      );
      listed.push(...page.tasks);
      after = page.nextPosition ?? undefined;
      if (tooLarge(listed.length, columns.length)) throw new ExportTooLargeError();
    } while (after !== undefined);
    const ctx = { t, selectedOrgId: authState.selectedOrg.id };
    const selection = listSelection(t, listed.length, filters);
    return exportRequest(columns, listed, (key, task) => taskCell(key, task, ctx), selection);
  };

  const errorMessage = (error: unknown, format: ListFormat): string => {
    if (!isTooLargeToDownload(error)) return String(t("The task list could not be downloaded. Try again."));
    return format === "PDF"
      ? String(t("Too many rows for a PDF. Choose Excel or CSV, or narrow the list with the filters."))
      : String(t("The list has too many tasks for one file. Narrow it with the filters."));
  };

  return (
    <Menu isLazy closeOnSelect={false} placement="bottom-end">
      {({ onClose }) => (
        <>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />}>
            {t("Download")}
          </MenuButton>
          <MenuList>
            <ListDownloadItems
              label={t("Task list")}
              endpoint="/api/contract-tasks/list-export"
              baseFileName={taskListFileName(new Date())}
              errorMessage={errorMessage}
              buildBody={buildBody}
              isLoading={isLoading}
              isDisabled={isLoading || failed || isEmpty}
              hint={isEmpty ? String(t("No tasks to download")) : undefined}
              onDone={onClose}
            />
          </MenuList>
        </>
      )}
    </Menu>
  );
};
